/**
 * @Description 机会点详情-开店区域概览-详情页集客点信息
 */

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'antd';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import { useSelector } from 'react-redux';
import { deepCopy, isArray, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { getDetail as getSpotDetail } from '@/common/api/sitemapgather';
import LinesInfo from '../LinesInfo';
import V2Title from '@/common/components/Feedback/V2Title';

const PlanSpotDetail: FC<any> = ({
  curCompValue,
}) => {
  const [relationDetail, setRelationDetail] = useState<any>({}); // 集客点相关完整详情
  const [spotDetail, setSpotDetail] = useState<any>({}); // 选中集客点的详情
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);

  /**
   * @description 是否益禾堂租户
   */
  const isYiHeTang = useMemo(() => {
    return tenantCheckData?.isYiHeTang;
  }, [tenantCheckData]);

  const targetPlanSpotIds = useMemo(() => {
    return curCompValue?.planSpotId ? [curCompValue.planSpotId] : [spotDetail?.name];
  }, [curCompValue, spotDetail]);

  /**
   * @description 初次渲染formItem信息
   */
  useEffect(() => {
    const modelClusterId = curCompValue?.clusterId;
    const planSpotId = curCompValue?.planSpotId;
    const savePlanSpot = curCompValue?.savePlanSpot;
    // 目前只有益禾堂租户需要展示集客点信息
    if (modelClusterId && isYiHeTang) {
      getSpotDetail({ modelClusterId }).then((res: any) => {
        if (!planSpotId && isNotEmptyAny(savePlanSpot) && isNotEmptyAny(savePlanSpot?.planSpots?.[0])) {
          // 如果有快捷创建的集客点信息，则取这里
          const _relationDetail = deepCopy(res);
          const _savePlanSpot = savePlanSpot.planSpots.map((itm: any) => ({ ...itm, id: itm.name }));
          _relationDetail.planSpots = Array.isArray(_relationDetail.planSpots) ? _relationDetail.planSpots.concat(_savePlanSpot) : _savePlanSpot; // 拼接 planSpots
          setRelationDetail(_relationDetail);
          setSpotDetail(savePlanSpot.planSpots[0]);
        } else {
          isNotEmptyAny(res) && setRelationDetail(res);
          const tmpSpot = isArray(res?.planSpots) ? res?.planSpots.find((itm) => itm.id === planSpotId) : null;
          isNotEmptyAny(tmpSpot) && setSpotDetail(tmpSpot);
        }
      });
    } else if (savePlanSpot && isNotEmptyAny(savePlanSpot)) {
      // 如果有快捷创建的集客点信息，则取这里
      if (isNotEmptyAny(savePlanSpot?.planSpots?.[0])) {
        setSpotDetail(savePlanSpot.planSpots[0]);
        setRelationDetail(savePlanSpot);
      }
    } else {
      setSpotDetail({});
    }
  }, [curCompValue, isYiHeTang]);

  /**
   * @description 集合点详情
   */
  const renderSpotRelation = () => {
    return <>
      <Col span={12}>
        <V2DetailItem
          label='商圈类型'
          value={spotDetail.thirdLevelCategory || spotDetail.secondLevelCategory || spotDetail.firstLevelCategory || '-'}
        />
      </Col>
      <Col span={24}>
        <V2DetailItem label='动线始末'>
          <LinesInfo
            detail={relationDetail}
            targetPlanSpotIds={targetPlanSpotIds}
          />
        </V2DetailItem>
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='首选店铺'
          value={spotDetail.firstShop}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='次选店铺'
          value={spotDetail.secondShop}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='租金单价行情'
          value={spotDetail.rent ? `${spotDetail.rent}㎡/月/元` : '-'}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='转让费行情'
          value={spotDetail.assignmentFee ? `${spotDetail.assignmentFee}元` : '-'}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='动线视频讲解'
          type='videos'
          assets={spotDetail.videoUrls}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='视频备注'
          type='textarea'
          value={spotDetail.videoRemark}
        />
      </Col>
      {
        isArray(spotDetail?.competitors) ? spotDetail.competitors.map((itm: any, idx: number) => (
          <React.Fragment key={idx}>
            <Col span={24}>
              <V2Title
                type='H2'
                divider
                text={spotDetail.competitors.length > 1 ? `竞品信息${idx + 1}` : '竞品信息'}
                className={'mt-24'}
              />
            </Col>
            <Col span={12}>
              <V2DetailItem
                label='竞品名称'
                value={itm.competitorName}
              />
            </Col>
            <Col span={12}>
              <V2DetailItem
                label='外卖月总单量'
                value={isNotEmpty(itm.takeAwayCount) ? `${itm.takeAwayCount}单` : '-'}
              />
            </Col>
          </React.Fragment>
        )) : <></>
      }
      {
        isArray(spotDetail?.landlords) ? spotDetail.landlords.map((itm: any, idx: number) => (
          <React.Fragment key={idx}>
            <Col span={24}>
              <V2Title
                type='H2'
                divider
                text={spotDetail.landlords.length > 1 ? `店铺信息${idx + 1}` : '店铺信息'}
                className={'mt-24'}
              />
            </Col>
            <Col span={12}>
              <V2DetailItem
                label='店铺名称'
                value={itm.landlordShopName}
              />
            </Col>
            <Col span={12}>
              <V2DetailItem
                label='联系人姓名'
                value={itm.landlordName}
              />
            </Col>
            <Col span={12}>
              <V2DetailItem
                label='联系方式'
                value={itm.landlordMobile}
              />
            </Col>
          </React.Fragment>
        )) : <></>
      }
    </>;
  };

  return (
    curCompValue?.planSpotName ? <Row gutter={24}>
      <Col span={24}>
        <V2Title
          type='H2'
          divider
          text={'集客点信息'}
          className={'mt-24'}
        />
      </Col>
      <Col span={12}>
        <V2DetailItem
          label='所在集客点'
          value={curCompValue?.planSpotName}
        />
      </Col>
      {isYiHeTang && isNotEmptyAny(spotDetail) ? renderSpotRelation() : <></>}
    </Row> : <></>
  );
};

export default PlanSpotDetail;
